import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Target, Heart, Zap, Users, Globe } from 'lucide-react';
import SEO from '../components/SEO';
import './About.css';

const TEAM_VALUES = [
  { icon: <Target size={24} />, title: 'Client-First', desc: 'Every decision begins with understanding your operational reality — not our product catalog.' },
  { icon: <Award size={24} />, title: 'Excellence', desc: 'We hold every line of code to the same standard: production-grade, compliant, performant.' },
  { icon: <Heart size={24} />, title: 'Partnership', desc: 'We measure success by your outcomes, not deliverables. Long-term relationships over quick wins.' },
  { icon: <Zap size={24} />, title: 'Innovation', desc: 'Continuous investment in modern architecture ensures your software stays relevant for decades.' },
];

const TEAM = [
  { name: 'Founding Team', role: 'Leadership', initials: 'SK', desc: 'With 27+ years guiding SHIVKMS from a two-person startup to a multi-product tech company.' },
  { name: 'Engineering', role: 'Development', initials: 'ENG', desc: '40+ developers specializing in enterprise Java, .NET, React, Node.js and cloud infrastructure.' },
  { name: 'Design & UX', role: 'Product', initials: 'DX', desc: 'Human-centered designers who turn complex workflows into intuitive software experiences.' },
  { name: 'Client Success', role: 'Support', initials: 'CS', desc: '24/7 support teams fluent in your industry — from SEBI regulations to school fee structures.' },
];

const MILESTONES = [
  { year: '1998', event: 'Founded in Delhi with a mission to democratize enterprise software for Indian businesses.' },
  { year: '2003', event: 'Launched first RTA (Registrar & Transfer Agent) platform, adopted by 5 financial firms.' },
  { year: '2008', event: 'Expanded into School Management; crossed 20 institutional clients.' },
  { year: '2012', event: 'Hotel Management Suite launched; entered hospitality sector across North India.' },
  { year: '2017', event: 'Achieved SEBI compliance certification; launched RESTful API platform.' },
  { year: '2022', event: 'Crossed 50 enterprise clients; launched E-Commerce & Scaffolding modules.' },
  { year: '2024', event: 'Launched cloud-native versions of all products with 99.9% SLA guarantee.' },
];

export default function About() {
  return (
    <div className="about-page">
      <SEO
        title="About Us — 27 Years of Building Business Software"
        description="SHIVKMS Multi Solution has delivered customized software since 1998. Learn about our mission, values, timeline and the team behind 10+ enterprise products."
        keywords="about SHIVKMS, software company history Delhi, enterprise software team India"
        path="/about"
      />
      {/* Hero */}
      <section className="about-hero">
        <div className="about-hero-bg" />
        <div className="container">
          <div className="about-hero-inner">
            <span className="eyebrow">Our Story</span>
            <h1>27 Years of Building<br /><span className="gradient-text">India's Business Software</span></h1>
            <p>SHIVKMS was born from a simple belief: that every business — not just Fortune 500s — deserves software that actually works. We've spent over two decades proving it.</p>
            <Link to="/contact" className="btn-primary">
              Work With Us <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="section-py">
        <div className="container">
          <div className="mission-grid">
            <div className="mission-text">
              <span className="eyebrow">Who We Are</span>
              <h2>More Than Software — <span className="gradient-text">We're Your Tech Partner</span></h2>
              <p>SHIVKMS Multi Solution (SHIVKMS) started in 1998 with a team of four developers in a small Delhi office. Today, we are a 100+ person technology company delivering critical software for finance, education, hospitality, construction, and e-commerce sectors across India.</p>
              <p style={{marginTop:'16px'}}>What hasn't changed is our core philosophy: get deeply close to each client's operational reality, build software that solves real problems, and stay by your side as your business evolves.</p>
              <div className="mission-stats">
                <div className="m-stat"><span className="m-stat-n">50+</span><span className="m-stat-l">Enterprise Clients</span></div>
                <div className="m-stat"><span className="m-stat-n">10+</span><span className="m-stat-l">Products Shipped</span></div>
                <div className="m-stat"><span className="m-stat-n">100+</span><span className="m-stat-l">Team Members</span></div>
              </div>
            </div>
            <div className="mission-visual">
              <div className="about-card-grid">
                <div className="about-accent-card">
                  <Globe size={32} className="aac-icon" />
                  <h3>Pan-India Reach</h3>
                  <p>Clients in Delhi, Haryana, UP, Rajasthan, and growing.</p>
                </div>
                <div className="about-accent-card accent-dark">
                  <Users size={32} className="aac-icon" />
                  <h3>Client-Centric Dev</h3>
                  <p>Every product roadmap is shaped directly by client feedback.</p>
                </div>
                <div className="about-accent-card accent-dark">
                  <Award size={32} className="aac-icon" />
                  <h3>SEBI Certified</h3>
                  <p>Fully compliant RTA platform trusted by financial institutions.</p>
                </div>
                <div className="about-accent-card">
                  <Zap size={32} className="aac-icon" />
                  <h3>99.9% Uptime SLA</h3>
                  <p>Cloud-native infrastructure engineered for reliability at scale.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-py values-section">
        <div className="values-bg" />
        <div className="container">
          <div className="section-heading">
            <span className="eyebrow">Our DNA</span>
            <h2>The Values We <span className="gradient-text">Build With</span></h2>
            <div className="divider" />
          </div>
          <div className="values-grid">
            {TEAM_VALUES.map((v, i) => (
              <div key={i} className="value-card card">
                <div className="value-icon">{v.icon}</div>
                <h3 className="value-title">{v.title}</h3>
                <p className="value-desc">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="section-py timeline-section">
        <div className="container">
          <div className="section-heading">
            <span className="eyebrow">Our Journey</span>
            <h2>From Startup to <span className="gradient-text">Enterprise Partner</span></h2>
            <div className="divider" />
          </div>
          <div className="timeline">
            {MILESTONES.map((m, i) => (
              <div key={i} className={`timeline-item ${i % 2 === 0 ? 'left' : 'right'}`}>
                <div className="timeline-content">
                  <span className="timeline-year">{m.year}</span>
                  <p className="timeline-event">{m.event}</p>
                </div>
                <div className="timeline-dot" />
              </div>
            ))}
            <div className="timeline-line" />
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="section-py">
        <div className="container">
          <div className="section-heading">
            <span className="eyebrow">Our People</span>
            <h2>Built by People Who <span className="gradient-text">Care Deeply</span></h2>
            <div className="divider" />
            <p>Four specialized teams working in harmony to deliver software that lasts.</p>
          </div>
          <div className="team-grid">
            {TEAM.map((t, i) => (
              <div key={i} className="team-card card">
                <div className="team-avatar">{t.initials}</div>
                <div className="team-role-tag">{t.role}</div>
                <h3 className="team-name">{t.name}</h3>
                <p className="team-desc">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="about-cta">
        <div className="about-cta-bg" />
        <div className="container" style={{textAlign:'center', position:'relative', zIndex:1}}>
          <h2>Ready to build something lasting?</h2>
          <p style={{color:'var(--text-secondary)', marginTop:'12px', marginBottom:'32px', fontSize:'1.05rem'}}>
            Let's talk about your business goals and how SHIVKMS can help you reach them.
          </p>
          <Link to="/contact" className="btn-primary">
            Get a Free Consultation <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
